/* Ordenação + filtro por cor (spec 0042 / ADR-020) + sold-out last (ADR-050) */
(() => {
  const SORT_ATTR = 'data-gm-sort';
  const SEM_COR_KEY = '__sem_cor__';
  const SEM_COR_LABEL = 'Sem cor';
  /** Saturação abaixo disso = neutro (preto / café / taupe / off-white). */
  const NEUTRAL_SAT = 0.18;
  /** Origem do arco-íris: vermelhos/malbec (~350°) antes de laranjas/ferrugem. */
  const HUE_ORIGIN = 350;

  /** Grids com sort UI + rails/listagens para sold-out last. */
  const sortGrids = () =>
    [
      ...document.querySelectorAll('.gm-collection-category__grid'),
      // Landing modelo (0050): grade via gm-product-rail
      ...document.querySelectorAll(
        '.gm-collection-modelo__grid [data-gm-rail-track]',
      ),
    ].filter((grid) => grid.querySelector('.gm-card'));

  const listingContainers = () =>
    [
      ...document.querySelectorAll('.gm-collection-category__grid'),
      ...document.querySelectorAll(
        '.gm-collection-modelo__grid [data-gm-rail-track]',
      ),
      ...document.querySelectorAll('.gm-product-rail [data-gm-rail-track]'),
      ...document.querySelectorAll('.gm-product-list__grid'),
    ].filter((grid) => grid.querySelector(':scope > .gm-card'));

  const allCards = () =>
    sortGrids().flatMap((grid) => [
      ...grid.querySelectorAll(':scope > .gm-card'),
    ]);

  const ensureOrder = (grid) => {
    [...grid.children].forEach((el, i) => {
      if (el instanceof HTMLElement && !el.dataset.gmSortOrder) {
        el.dataset.gmSortOrder = String(i);
      }
    });
  };

  const read = (card, key) => {
    const v = card.getAttribute(`data-gm-sort-${key}`) || '';
    return v.trim();
  };

  const isAvailable = (card) =>
    card.getAttribute('data-gm-available') !== '0';

  const corKey = (card) => {
    const cor = read(card, 'cor');
    return cor || SEM_COR_KEY;
  };

  const corLabel = (key) => (key === SEM_COR_KEY ? SEM_COR_LABEL : key);

  const modeloKey = (card) => {
    const m = read(card, 'modelo');
    if (m) return m;
    return `__solo_${card.dataset.gmSortOrder || '0'}`;
  };

  const modeloRankFor = (grid) => {
    const rank = new Map();
    for (const card of grid.querySelectorAll(':scope > .gm-card')) {
      const key = modeloKey(card);
      const idx = Number(card.dataset.gmSortOrder || 0);
      if (!rank.has(key)) rank.set(key, idx);
    }
    return rank;
  };

  const parseHex = (hex) => {
    let h = String(hex || '')
      .trim()
      .replace(/^#/, '');
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    const n = parseInt(h, 16);
    return {
      r: ((n >> 16) & 255) / 255,
      g: ((n >> 8) & 255) / 255,
      b: (n & 255) / 255,
    };
  };

  const relativeLum = ({ r, g, b }) => {
    const lin = (c) =>
      c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  };

  const rgbToHsl = ({ r, g, b }) => {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    if (d === 0) return { h: 0, s: 0, l };
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
    return { h: h * 360, s, l };
  };

  /**
   * Degradê semântico: neutros (ramp de luminância) depois cromáticos
   * por matiz (malbec → ferrugem → verdes → azuis), luminância por último.
   * bucket: 0 neutro, 1 hex cromático, 2 textura/nome sem hex, 3 sem cor.
   */
  const colorMetrics = (hex, key) => {
    if (key === SEM_COR_KEY) {
      return { bucket: 3, hue: 0, lum: 1, name: SEM_COR_LABEL };
    }
    const rgb = parseHex(hex);
    if (!rgb) {
      return { bucket: 2, hue: 0, lum: 1, name: corLabel(key) };
    }
    const { h, s } = rgbToHsl(rgb);
    const lum = relativeLum(rgb);
    if (s < NEUTRAL_SAT) {
      return { bucket: 0, hue: 0, lum, name: corLabel(key) };
    }
    const hue = (h - HUE_ORIGIN + 360) % 360;
    return { bucket: 1, hue, lum, name: corLabel(key) };
  };

  const metricsForCard = (card) =>
    colorMetrics(read(card, 'hex'), corKey(card));

  const compareColorKeys = (ma, mb, lumDir) => {
    if (ma.bucket !== mb.bucket) return ma.bucket - mb.bucket;
    if (ma.bucket === 1 && ma.hue !== mb.hue) return ma.hue - mb.hue;
    if (ma.lum !== mb.lum) return (ma.lum - mb.lum) * lumDir;
    return ma.name.localeCompare(mb.name, 'pt-BR', { sensitivity: 'base' });
  };

  /** @type {string} */
  let sortMode = 'modelo';
  /** @type {Set<string>} */
  let activeColors = new Set();

  const originalOrder = (a, b) =>
    Number(a.dataset.gmSortOrder || 0) - Number(b.dataset.gmSortOrder || 0);

  const compareModelo = (a, b, rank) => {
    const ra = rank.get(modeloKey(a)) ?? 0;
    const rb = rank.get(modeloKey(b)) ?? 0;
    if (ra !== rb) return ra - rb;
    return originalOrder(a, b);
  };

  const compare = (mode, a, b, rank) => {
    // ADR-050: available first; sold-out always last
    const availCmp = Number(isAvailable(b)) - Number(isAvailable(a));
    if (availCmp !== 0) return availCmp;

    if (mode === 'manual') {
      return originalOrder(a, b);
    }
    if (mode === 'modelo') {
      return compareModelo(a, b, rank);
    }
    if (mode === 'title-asc' || mode === 'title-desc') {
      const cmp = read(a, 'title').localeCompare(read(b, 'title'), 'pt-BR', {
        sensitivity: 'base',
      });
      return mode === 'title-asc' ? cmp : -cmp;
    }
    if (mode === 'price-asc' || mode === 'price-desc') {
      const pa = Number(read(a, 'price') || 0);
      const pb = Number(read(b, 'price') || 0);
      return mode === 'price-asc' ? pa - pb : pb - pa;
    }
    if (mode === 'color-dark' || mode === 'color-light' || mode === 'color') {
      const lumDir = mode === 'color-light' ? -1 : 1;
      const byColor = compareColorKeys(
        metricsForCard(a),
        metricsForCard(b),
        lumDir,
      );
      if (byColor !== 0) return byColor;
      return compareModelo(a, b, rank);
    }
    return 0;
  };

  // appendChild em track com scroll-snap pode ancorar scrollLeft no fim — resetar.
  const resetRailScroll = (track) => {
    const viewport =
      track.closest('[data-gm-rail-viewport]') ||
      track.closest('.gm-product-rail__viewport') ||
      track.parentElement;
    if (!viewport || viewport === track) return;
    const prevBehavior = viewport.style.scrollBehavior;
    viewport.style.scrollBehavior = 'auto';
    viewport.scrollLeft = 0;
    viewport.style.scrollBehavior = prevBehavior;
    viewport.dispatchEvent(new Event('scroll', { bubbles: false }));
  };

  const reorderSoldOutLast = (grid, mode = 'manual') => {
    ensureOrder(grid);
    const rank = modeloRankFor(grid);
    const cards = [...grid.querySelectorAll(':scope > .gm-card')];
    if (cards.length < 2) return;
    const sorted = [...cards].sort((a, b) => compare(mode, a, b, rank));
    const unchanged = sorted.every((card, i) => card === cards[i]);
    if (unchanged) return;
    for (const card of sorted) grid.appendChild(card);
    if (grid.matches('[data-gm-rail-track]')) resetRailScroll(grid);
  };

  const updateCount = () => {
    const cards = allCards();
    // Sem grade contável (ex.: Bolsas editorial com rails): mantém o count do Liquid
    if (!cards.length) return;
    const visible = cards.filter((c) => !c.hidden).length;
    document.querySelectorAll('[data-gm-toolbar-count]').forEach((el) => {
      el.textContent = String(visible);
    });
  };

  const applyFilter = () => {
    const cards = allCards();
    const filtering = activeColors.size > 0;
    for (const card of cards) {
      const key = corKey(card);
      card.hidden = filtering && !activeColors.has(key);
    }
    updateCount();

    const badge = document.querySelector('[data-gm-filter-badge]');
    const clearBtn = document.querySelector('[data-gm-filter-clear]');
    if (badge) {
      if (filtering) {
        badge.hidden = false;
        badge.textContent = String(activeColors.size);
      } else {
        badge.hidden = true;
        badge.textContent = '';
      }
    }
    if (clearBtn) clearBtn.hidden = !filtering;
  };

  const applySort = () => {
    for (const grid of sortGrids()) {
      reorderSoldOutLast(grid, sortMode);
    }
  };

  const apply = () => {
    applySort();
    applyFilter();
  };

  const collectColors = () => {
    /** @type {Map<string, { key: string, label: string, hex: string, texture: string, count: number }>} */
    const map = new Map();
    for (const card of allCards()) {
      const key = corKey(card);
      const hex = read(card, 'hex');
      const texture = read(card, 'texture');
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: corLabel(key),
          hex,
          texture,
          count: 0,
        });
      }
      const entry = map.get(key);
      entry.count += 1;
      if (!entry.hex && hex) entry.hex = hex;
      if (!entry.texture && texture) entry.texture = texture;
    }
    const list = [...map.values()];
    list.sort((a, b) =>
      compareColorKeys(
        colorMetrics(a.hex, a.key),
        colorMetrics(b.hex, b.key),
        1,
      ),
    );
    return list;
  };

  const escapeHtml = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const swatchStyle = (entry) => {
    if (entry.texture) {
      return `background-image:url('${escapeHtml(entry.texture)}')`;
    }
    if (entry.hex) return `--gm-swatch-color:${escapeHtml(entry.hex)}`;
    return '';
  };

  const renderFilterSwatches = () => {
    const host = document.querySelector('[data-gm-filter-swatches]');
    if (!host) return;
    const colors = collectColors();
    if (!colors.length) {
      host.innerHTML =
        '<p class="gm-collection-category__filter-empty">Nenhuma cor nesta página.</p>';
      return;
    }

    host.innerHTML = colors
      .map((entry) => {
        const on = activeColors.has(entry.key);
        const isSem = entry.key === SEM_COR_KEY;
        const style = swatchStyle(entry);
        const mode = isSem ? 'empty' : entry.texture ? 'texture' : 'solid';
        const swatchClass = [
          'gm-color-swatch',
          'gm-color-swatch--md',
          `gm-color-swatch--${mode}`,
        ].join(' ');

        return `<button
          type="button"
          class="gm-collection-category__filter-chip${on ? ' is-active' : ''}"
          data-gm-filter-cor="${escapeHtml(entry.key)}"
          aria-pressed="${on ? 'true' : 'false'}"
          title="${escapeHtml(entry.label)} (${entry.count})"
        >
          <span class="${swatchClass}" style="${style}" aria-hidden="true">${
            isSem ? '<span class="gm-color-swatch__x"></span>' : ''
          }</span>
          <span class="gm-collection-category__filter-chip-label">${escapeHtml(entry.label)}</span>
          <span class="gm-collection-category__filter-chip-count">${entry.count}</span>
        </button>`;
      })
      .join('');
  };

  const setActiveSort = (menu, activeBtn) => {
    menu.querySelectorAll('[data-gm-sort]').forEach((btn) => {
      const on = btn === activeBtn;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  };

  document.querySelectorAll('[data-gm-collection-sort]').forEach((details) => {
    const menu = details.querySelector('.gm-collection-category__sort-menu');
    if (!menu) return;

    menu.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gm-sort]');
      if (!(btn instanceof HTMLElement)) return;
      sortMode = btn.getAttribute(SORT_ATTR) || 'manual';
      setActiveSort(menu, btn);
      apply();
      details.open = false;
    });
  });

  document.querySelectorAll('[data-gm-collection-filter]').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (details.open) renderFilterSwatches();
    });
  });

  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-gm-filter-cor]');
    if (chip instanceof HTMLElement) {
      const key = chip.getAttribute('data-gm-filter-cor') || '';
      if (!key) return;
      if (activeColors.has(key)) activeColors.delete(key);
      else activeColors.add(key);
      renderFilterSwatches();
      apply();
      return;
    }

    const clear = e.target.closest('[data-gm-filter-clear]');
    if (clear) {
      activeColors.clear();
      renderFilterSwatches();
      apply();
    }
  });

  const toolbarDetails = () =>
    document.querySelectorAll(
      '[data-gm-collection-sort][open], [data-gm-collection-filter][open]',
    );

  document.addEventListener('click', (e) => {
    const node = e.target;
    if (!(node instanceof Node)) return;
    toolbarDetails().forEach((d) => {
      if (!d.contains(node)) d.open = false;
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    toolbarDetails().forEach((d) => {
      d.open = false;
    });
  });

  // Fecha outros details ao abrir um (ordenar / filtrar)
  document.addEventListener(
    'toggle',
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLDetailsElement)) return;
      if (
        !t.matches('[data-gm-collection-sort], [data-gm-collection-filter]') ||
        !t.open
      ) {
        return;
      }
      document
        .querySelectorAll(
          '[data-gm-collection-sort][open], [data-gm-collection-filter][open]',
        )
        .forEach((d) => {
          if (d !== t) d.open = false;
        });
    },
    true,
  );

  // Rails: ordem original + sold-out last. Grades de coleção: agrupa por modelo.
  listingContainers().forEach((grid) => reorderSoldOutLast(grid, 'manual'));
  sortGrids().forEach(ensureOrder);
  apply();
})();
